import React, { useState, useEffect, useRef } from 'react';

function Settings({ userSettings, setUserSettings }) {
  const [formData, setFormData] = useState(() => {
    if (userSettings.defaultRegistratieDagen) return userSettings;
    const defaultRegistratieDagen = {};
    (userSettings.defaultThuiswerkDagen || []).forEach(dag => {
      defaultRegistratieDagen[dag] = 'thuis';
    });
    return { ...userSettings, defaultRegistratieDagen };
  });
  const [showKmWarning, setShowKmWarning] = useState(false);
  const [showThuisWarning, setShowThuisWarning] = useState(false);
  const [showKmZakelijkWarning, setShowKmZakelijkWarning] = useState(false);
  const [toastVisible, setToastVisible] = useState(false);
  const saveTimerRef = useRef(null);
  const toastTimerRef = useRef(null);
  const isFirstRender = useRef(true);

  const saveNow = () => {
    clearTimeout(saveTimerRef.current);
    setUserSettings(formData);
    setToastVisible(true);
    clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToastVisible(false), 2500);
  };

  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return; }
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(saveNow, 600);
    return () => clearTimeout(saveTimerRef.current);
  }, [formData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const normalizedValue = value.replace(',', '.');
    if (name === 'vergoedingPerKm') {
      setShowKmWarning(value !== '' && parseFloat(normalizedValue) !== 0.23);
    }
    if (name === 'vergoedingPerKmZakelijk') {
      setShowKmZakelijkWarning(value !== '' && parseFloat(normalizedValue) !== 0.23);
    }
    if (name === 'thuiswerkVergoeding') {
      setShowThuisWarning(value !== '' && parseFloat(normalizedValue) !== 2.45);
    }
    const isNumeric = name.includes('km') || name.includes('vergoeding') || name.includes('Km') || name.includes('Vergoeding') || name.includes('Bedrag') || name.includes('bedrag');
    setFormData(prev => ({
      ...prev,
      [name]: isNumeric ? (value === '' ? '' : parseFloat(normalizedValue)) : value
    }));
  };

  const handleDefaultRegistratieChange = (dagNr, value) => {
    setFormData(prev => {
      const nieuw = { ...(prev.defaultRegistratieDagen || {}) };
      if (value === '') delete nieuw[dagNr];
      else nieuw[dagNr] = value;
      const thuisDagen = Object.entries(nieuw)
        .filter(([, v]) => v === 'thuis')
        .map(([k]) => parseInt(k, 10));
      return { ...prev, defaultRegistratieDagen: nieuw, defaultThuiswerkDagen: thuisDagen };
    });
  };

  const handleWerkdagToggle = (dagNummer) => {
    setFormData(prev => {
      const huidig = prev.werkdagen ?? [1, 2, 3, 4, 5];
      const nieuw = huidig.includes(dagNummer)
        ? huidig.filter(d => d !== dagNummer)
        : [...huidig, dagNummer];
      return { ...prev, werkdagen: nieuw };
    });
  };

  const weekdagen = [
    { nr: 1, naam: 'Maandag' },
    { nr: 2, naam: 'Dinsdag' },
    { nr: 3, naam: 'Woensdag' },
    { nr: 4, naam: 'Donderdag' },
    { nr: 5, naam: 'Vrijdag' },
  ];

  return (
    <>
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="bg-gradient-to-r from-blue-700 to-blue-800 px-6 py-8 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-white">Persoonlijke Instellingen</h2>
          <span className={`text-sm font-medium px-3 py-1 rounded-full bg-green-400 text-white transition-opacity duration-500 ${toastVisible ? 'opacity-100' : 'opacity-0'}`}>
            Opgeslagen
          </span>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">Woon-werk verkeer</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="kmPerRitFiets" className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 mb-2">
                  Enkele reisafstand fiets (km)
                  <span className="relative group inline-flex">
                    <span className="w-4 h-4 rounded-full bg-blue-100 text-blue-600 text-xs flex items-center justify-center cursor-default select-none font-bold">i</span>
                    <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-64 bg-gray-800 text-white text-xs rounded-lg px-3 py-2 opacity-0 group-hover:opacity-100 transition pointer-events-none z-10 shadow-lg">
                      Vul hier de enkele reisafstand in (thuis → kantoor). De retourrit wordt automatisch meeberekend (2×).
                      <span className="absolute left-1/2 -translate-x-1/2 top-full border-4 border-transparent border-t-gray-800"></span>
                    </span>
                  </span>
                </label>
                <input
                  type="number"
                  id="kmPerRitFiets"
                  name="kmPerRitFiets"
                  value={formData.kmPerRitFiets}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                />
                {formData.kmPerRitFiets > 0 && parseFloat(formData.kmPerRitFiets) <= 10 && (
                  <p className="mt-2 text-sm text-yellow-700 bg-yellow-50 border border-yellow-300 rounded-lg px-3 py-2">
                    Minimale afstand voor vergoeding is 10 km enkele reis.
                  </p>
                )}
                {formData.kmPerRitFiets > 10 && (
                  <p className="mt-1.5 text-xs text-blue-600 flex items-center gap-1">
                    <span>↔</span>
                    <span>Retour: <strong>{(parseFloat(formData.kmPerRitFiets) * 2).toLocaleString('nl-NL', { maximumFractionDigits: 2 })} km</strong> per dag</span>
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="kmPerRitAuto" className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 mb-2">
                  Enkele reisafstand auto (km)
                  <span className="relative group inline-flex">
                    <span className="w-4 h-4 rounded-full bg-blue-100 text-blue-600 text-xs flex items-center justify-center cursor-default select-none font-bold">i</span>
                    <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-64 bg-gray-800 text-white text-xs rounded-lg px-3 py-2 opacity-0 group-hover:opacity-100 transition pointer-events-none z-10 shadow-lg">
                      Vul hier de enkele reisafstand in (thuis → kantoor). De retourrit wordt automatisch meeberekend (2×).
                      <span className="absolute left-1/2 -translate-x-1/2 top-full border-4 border-transparent border-t-gray-800"></span>
                    </span>
                  </span>
                </label>
                <input
                  type="number"
                  id="kmPerRitAuto"
                  name="kmPerRitAuto"
                  value={formData.kmPerRitAuto}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                />
                {formData.kmPerRitAuto > 0 && parseFloat(formData.kmPerRitAuto) <= 10 && (
                  <p className="mt-2 text-sm text-yellow-700 bg-yellow-50 border border-yellow-300 rounded-lg px-3 py-2">
                    Minimale afstand voor vergoeding is 10 km enkele reis.
                  </p>
                )}
                {formData.kmPerRitAuto > 10 && (
                  <p className="mt-1.5 text-xs text-blue-600 flex items-center gap-1">
                    <span>↔</span>
                    <span>Retour: <strong>{(parseFloat(formData.kmPerRitAuto) * 2).toLocaleString('nl-NL', { maximumFractionDigits: 2 })} km</strong> per dag</span>
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="vergoedingPerKm" className="block text-sm font-semibold text-gray-700 mb-2">
                  Vergoeding per km
                </label>
                <input
                  type="number"
                  id="vergoedingPerKm"
                  name="vergoedingPerKm"
                  value={formData.vergoedingPerKm}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                />
                {showKmWarning && (
                  <p className="mt-2 text-sm text-yellow-700 bg-yellow-50 border border-yellow-300 rounded-lg px-3 py-2">
                    De km-vergoeding is in de CAO vastgelegd op € 0,23.
                  </p>
                )}
              </div>

            </div>
          </div>

          <div className="border-t pt-6">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">OV reizen</p>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-sm font-semibold text-gray-700">NS Businesscard</span>
              <span className="relative group inline-flex">
                <span className="w-4 h-4 rounded-full bg-blue-100 text-blue-600 text-xs flex items-center justify-center cursor-default select-none font-bold">i</span>
                <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-72 bg-gray-800 text-white text-xs rounded-lg px-3 py-2 opacity-0 group-hover:opacity-100 transition pointer-events-none z-10 shadow-lg">
                  <span className="block mb-1"><strong>Met NS Businesscard:</strong> OV-reizen worden via de businesscard vergoed. Je hoeft geen bedragen in te vullen in de registratie.</span>
                  <span className="block"><strong>Zonder NS Businesscard:</strong> Je kunt per OV-dag het reisbedrag zelf invoeren in de registratie, zodat dit meegenomen wordt in je declaratie.</span>
                  <span className="absolute left-1/2 -translate-x-1/2 top-full border-4 border-transparent border-t-gray-800"></span>
                </span>
              </span>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, heeftNsBusinesscard: true }))}
                className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition ${
                  (formData.heeftNsBusinesscard ?? true)
                    ? 'bg-blue-600 border-blue-600 text-white'
                    : 'bg-white border-gray-300 text-gray-700 hover:border-blue-400'
                }`}
              >
                Ja, ik heb een NS Businesscard
              </button>
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, heeftNsBusinesscard: false }))}
                className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition ${
                  !(formData.heeftNsBusinesscard ?? true)
                    ? 'bg-blue-600 border-blue-600 text-white'
                    : 'bg-white border-gray-300 text-gray-700 hover:border-blue-400'
                }`}
              >
                Nee, ik heb geen NS Businesscard
              </button>
            </div>
            {!(formData.heeftNsBusinesscard ?? true) && (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="ovBedragEnkelReis" className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 mb-2">
                    OV bedrag enkele reis (€)
                    <span className="relative group inline-flex">
                      <span className="w-4 h-4 rounded-full bg-blue-100 text-blue-600 text-xs flex items-center justify-center cursor-default select-none font-bold">i</span>
                      <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-64 bg-gray-800 text-white text-xs rounded-lg px-3 py-2 opacity-0 group-hover:opacity-100 transition pointer-events-none z-10 shadow-lg">
                        Vul hier het OV-bedrag in voor een enkele reis (thuis → kantoor). De retourrit wordt automatisch meeberekend (2×) en als standaard ingevuld bij OV-dagen.
                        <span className="absolute left-1/2 -translate-x-1/2 top-full border-4 border-transparent border-t-gray-800"></span>
                      </span>
                    </span>
                  </label>
                  <input
                    type="number"
                    id="ovBedragEnkelReis"
                    name="ovBedragEnkelReis"
                    value={formData.ovBedragEnkelReis ?? ''}
                    onChange={handleChange}
                    step="0.01"
                    min="0"
                    placeholder="0,00"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  />
                  {(formData.ovBedragEnkelReis > 0) && (
                    <p className="mt-1.5 text-xs text-blue-600 flex items-center gap-1">
                      <span>↔</span>
                      <span>Retour: <strong>€ {(parseFloat(formData.ovBedragEnkelReis) * 2).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong> per dag</span>
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="border-t pt-6">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">Zakelijke reizen</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="vergoedingPerKmZakelijk" className="block text-sm font-semibold text-gray-700 mb-2">
                  Vergoeding per km zakelijke reis
                </label>
                <input
                  type="number"
                  id="vergoedingPerKmZakelijk"
                  name="vergoedingPerKmZakelijk"
                  value={formData.vergoedingPerKmZakelijk ?? 0.23}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                />
                {showKmZakelijkWarning && (
                  <p className="mt-2 text-sm text-yellow-700 bg-yellow-50 border border-yellow-300 rounded-lg px-3 py-2">
                    De km-vergoeding is in de CAO vastgelegd op € 0,23.
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="border-t pt-6">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">Thuiswerken</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="thuiswerkVergoeding" className="block text-sm font-semibold text-gray-700 mb-2">
                  Thuiswerkvergoeding per dag
                </label>
                <input
                  type="number"
                  id="thuiswerkVergoeding"
                  name="thuiswerkVergoeding"
                  value={formData.thuiswerkVergoeding}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                />
                {showThuisWarning && (
                  <p className="mt-2 text-sm text-yellow-700 bg-yellow-50 border border-yellow-300 rounded-lg px-3 py-2">
                    De thuiswerkvergoeding is in de CAO vastgelegd op € 2,45.
                  </p>
                )}
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Werkdagen
            </label>
            <p className="text-xs text-gray-500 mb-3">
              Selecteer de dagen waarop je werkt. Niet-geselecteerde dagen worden automatisch op vrij gezet in de registratie.
            </p>
            <div className="flex flex-wrap gap-3">
              {weekdagen.map(dag => {
                const geselecteerd = (formData.werkdagen ?? [1, 2, 3, 4, 5]).includes(dag.nr);
                return (
                  <button
                    key={dag.nr}
                    type="button"
                    onClick={() => handleWerkdagToggle(dag.nr)}
                    className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition ${
                      geselecteerd
                        ? 'bg-blue-600 border-blue-600 text-white'
                        : 'bg-white border-gray-300 text-gray-700 hover:border-blue-400'
                    }`}
                  >
                    {dag.naam}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Standaard registratie per werkdag
            </label>
            <p className="text-xs text-gray-500 mb-3">
              Kies per werkdag wat standaard wordt ingevuld in de registratie. Je kunt dit altijd per dag handmatig aanpassen.
            </p>
            <div className="space-y-2">
              {weekdagen.filter(d => (formData.werkdagen ?? [1, 2, 3, 4, 5]).includes(d.nr)).map(dag => {
                const waarde = (formData.defaultRegistratieDagen || {})[dag.nr] || '';
                return (
                  <div key={dag.nr} className="flex items-center gap-4">
                    <span className="text-sm font-medium text-gray-700 w-28">{dag.naam}</span>
                    <select
                      value={waarde}
                      onChange={(e) => handleDefaultRegistratieChange(dag.nr, e.target.value)}
                      className="px-3 py-2 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white"
                    >
                      <option value="">— geen standaard —</option>
                      <option value="ov">OV</option>
                      <option value="fiets">Fiets</option>
                      <option value="auto">Auto</option>
                      <option value="thuis">Thuis</option>
                    </select>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="border-t pt-6 flex justify-end">
            <button
              type="button"
              onClick={saveNow}
              className="px-6 py-3 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition"
            >
              Opslaan
            </button>
          </div>
        </div>
      </div>
    </div>

    {toastVisible && (
      <div className="fixed bottom-6 right-6 z-50 flex items-center gap-3 bg-green-600 text-white px-5 py-3 rounded-xl shadow-xl animate-fade-in">
        <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
        <span className="font-medium">Instellingen opgeslagen</span>
      </div>
    )}
    </>
  );
}

export default Settings;
